import { Card } from "@components/Card";
import { Feed } from "@components/feed";
import styled from "@emotion/styled";
import React, { useCallback } from "react";

import { FeedPage } from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";
import { ListParams } from "@every.org/common/src/routes/index";
import {
  CAUSE_FEED_MAX_PAGE_NUM,
  CAUSE_FEED_PAGE_SIZE,
} from "@every.org/common/src/routes/publicCached";

import { Link } from "src/components/Link";
import { fetchTagRecommendations } from "src/context/DonationsContext/actions";
import { colorCssVars } from "src/theme/color";
import { spacing, verticalStackCss } from "src/theme/spacing";

const StyledCard = styled(Card)`
  margin-top: -${spacing.l};
  margin-bottom: ${spacing.l};
  color: var(${colorCssVars.text.secondary});
`;

export type FeedItems = Awaited<
  ReturnType<typeof fetchTagRecommendations>
>["items"];

export interface TagTabProps {
  initialFeedItems?: FeedItems;
  tagName: string;
  onNoMoreItems: () => void;
}

export const TagNonprofitsTab: React.FCC<TagTabProps> = ({
  tagName,
  initialFeedItems,
  onNoMoreItems,
}) => {
  const loadMoreItems = useCallback(
    async ({ take, skip }: ListParams) => {
      const page = Math.min(
        Math.floor((skip + take - 1) / CAUSE_FEED_PAGE_SIZE),
        CAUSE_FEED_MAX_PAGE_NUM
      );
      const { items } = await fetchTagRecommendations(tagName, page);
      const hasMore =
        items.length === CAUSE_FEED_PAGE_SIZE && page < CAUSE_FEED_MAX_PAGE_NUM;
      return {
        items,
        hasMore,
      };
    },
    [tagName]
  );

  return (
    <div css={verticalStackCss.xxxxl}>
      <Feed
        initialItems={initialFeedItems}
        page={FeedPage.TAG_NONPROFITS}
        loadMoreItems={loadMoreItems}
        onNoMoreItems={onNoMoreItems}
      />
      <StyledCard>
        Know a nonprofit that belongs here? Please{" "}
        <Link
          to={getRoutePath({
            name: ClientRouteName.EDIT_TRENDING_CAUSE,
            tokens: { tagName },
            format: URLFormat.RELATIVE,
          })}
          data-tname="addCauseLink"
        >
          add to this page
        </Link>
        !
      </StyledCard>
    </div>
  );
};
